/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import { Node } from '@msdyn365-commerce-modules/utilities';
import IncrementalQuantity from './IncrementalQuantity';
import { IGridSettings, IImageSettings, IComponentProps } from '@msdyn365-commerce/core';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    ProductAvailableQuantity,
    ProductDimension,
    SimpleProduct,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductType,
    ProductPrice
} from '@msdyn365-commerce/retail-proxy';

import { OrderDetailsProduct } from '@msdyn365-commerce/components';
import { ICartActionResult, getCartState } from '@msdyn365-commerce/global-state';
//import { IProductCollectionData } from './product-collection.data';

export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}
export interface IAddToCartComponentProps extends IComponentProps<{ product: SimpleProduct; price?: ProductPrice } | undefined> {
    context: any;
    className?: string;
    product: any;
    outOfStockText?: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    orderDetailsProducts?: OrderDetailsProduct[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;

    dialogStrings?: IAddToCartResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;
    cartData?: any;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
}
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}
export interface IProductCollectionState {
    currentProductqQty?: any;
}
//const defaultQuantity = 1;

class ProductCollectionQty extends React.PureComponent<IAddToCartComponentProps, IProductCollectionState> {
    constructor(props: IAddToCartComponentProps) {
        super(props);
        this.state = {
            currentProductqQty: ''
        };
    }
    public removeITem = () => {
        void getCartState(this.props.context.actionContext).then((result: any): boolean => {
            const recordId = this.props.product.ItemId;
            void this.removeItemFromCart(this.props, recordId);
            this.setState({ currentProductqQty: -1 });
            return false;
        });
    };
    public removeItemFromCart = async (props: IAddToCartComponentProps, LineID: any): Promise<void> => {
        const cartState = await getCartState(props.context.actionContext);
        const productInfow = await this.getAddToCartInputFromProps(props);
        const cartLinesData = cartState.cart.CartLines;
        const findData = cartLinesData?.find(cartLinesData => cartLinesData.ItemId === productInfow[0].product.ItemId);
        const input = {
            cartLineIds: [findData?.LineId!]
        };
        await cartState.removeCartLines(input);
    };
    public getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
        const defaultQuantity = 1;
        const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
        const input = products?.map(product => {
            return {
                product: product.simpleProduct!,
                count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
                catalogId: product.salesLine.CatalogId
            };
        });

        if (ArrayExtensions.hasElements(input)) {
            return input;
        }

        if (ArrayExtensions.hasElements(props.products)) {
            const actionContext = props.context.actionContext;
            const apiSettings = actionContext.requestContext.apiSettings;

            const catalogId = props.catalogId;
            const searchCriteriaInput: ProductSearchCriteria = {
                Context: {
                    ChannelId: apiSettings.channelId,
                    CatalogId: catalogId
                },
                IncludeAttributes: false,
                Ids: props.products.map(product => product.productId)
            };

            const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
            const productSearchResultsWithImages = searchResult.map(productSearchResult => {
                const newProductSearchResult = { ...productSearchResult };
                const newImageUrl = ' generateProductImageUrl(newProductSearchResult, apiSettings);';

                if (newImageUrl) {
                    newProductSearchResult.PrimaryImageUrl = newImageUrl;
                }

                return newProductSearchResult;
            });

            return props.products.map(item => {
                const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
                let simpleProduct: SimpleProduct;
                if (productData) {
                    simpleProduct = {
                        ...productData,
                        ProductTypeValue: ProductType.Variant,
                        AdjustedPrice: productData.Price,
                        BasePrice: productData.BasePrice ?? productData.Price
                    };
                } else {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                    simpleProduct = {
                        RecordId: item.productId
                    } as SimpleProduct;
                }

                return {
                    product: simpleProduct,
                    count: item.quantity,
                    catalogId: item.catalogId
                };
            });
        }

        if (props.data?.product) {
            return [
                {
                    product: props.data.product,
                    count: props.quantity ?? defaultQuantity,
                    catalogId: props.catalogId
                }
            ];
        }

        return [];
    };
    public onChangeHandler = async (newValue: number): Promise<boolean> => {
        if (newValue === -1) {
            this.removeITem();
        }
        const productID = this.props.data?.product.RecordId;
        let totalAvailableQuantity = 3;
        if (productID) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const axios = require('axios');
            const data = JSON.stringify({
                searchCriteria: {
                    ProductIds: [productID],
                    QuantityUnitTypeValue: 2,
                    DefaultWarehouseOnly: true
                }
            });

            const config = {
                method: 'post',
                maxBodyLength: Infinity,
                // url: 'https://scudisf0ajx86631475-rs.su.retail.dynamics.com/Commerce/Products/GetEstimatedAvailability?api-version=7.3',
                url: 'https://scuh5bsct3g80746264-rs.su.retail.dynamics.com/Commerce/Products/GetEstimatedAvailability?api-version=7.3',
                headers: {
                    accept: 'application/json',
                    'accept-language': 'en-US',
                    'cache-control': 'no-cache',
                    'content-type': 'application/json',
                    oun: '00000001'
                },
                data: data
            };

            await axios
                .request(config)
                .then((response: { data: any }) => {
                    if (
                        response.data?.ProductWarehouseInventoryAvailabilities &&
                        response.data?.ProductWarehouseInventoryAvailabilities.length > 0
                    ) {
                        const respObject = response.data?.ProductWarehouseInventoryAvailabilities.filter((item: any) => {
                            return item.ProductId === productID;
                        });
                        if (respObject && respObject.length > 0) {
                            totalAvailableQuantity = Math.floor((Number(respObject[0]?.PhysicalAvailable) * 80) / 100);
                        }
                    }
                })
                .catch((error: any) => {
                    console.log(error);
                });
        }
        console.log('this nowwww respObject : ', newValue, '===', totalAvailableQuantity);

        if (newValue > totalAvailableQuantity) {
            return false;
        }
        const cartState = getCartState(this.props.context.actionContext).then((result: any): boolean => {
            const recordId = this.props.product.ItemId;
            const cartLinesData = result._cart.CartLines;
            const findData = cartLinesData.find((cartLinesData: { ItemId: any }) => cartLinesData.ItemId === recordId);
            this.setState({ currentProductqQty: newValue });
            if (findData === undefined) {
                //   console.log(findData.Quantity);
                void this.addOneItemToCart(this.props);
                return true;
            }
            if (!result.isEmpty) {
                void this.updateCartId(this.props, findData.Quantity, newValue);
                return true;
            } else {
                void this.addOneItemToCart(this.props);
                //return true;
            }
            return false;
        });
        console.log(cartState);

        return true;
    };
    public updateCartId = async (props: IAddToCartComponentProps, oldQty: any, newQty: any): Promise<void> => {
        const cartState = await getCartState(props.context.actionContext);
        const productInfow = await this.getAddToCartInputFromProps(props);
        const cartLinesData = cartState.cart.CartLines;
        const findData = cartLinesData?.find(cartLinesData => cartLinesData.ItemId === productInfow[0].product.ItemId);
        const addToCartResult = await cartState.updateCartLineQuantity({
            cartLineId: findData?.LineId || '',
            newQuantity: newQty
        });
        console.log(addToCartResult);
    };
    /**
     * Add one item to cart when it is not buy again.
     * @param props -- The props.
     * @param setDisabled -- The set disable call back.
     * @param openModal -- The open modal call back.
     */
    public addOneItemToCart = async (props: IAddToCartComponentProps): Promise<void> => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
        const addToCartInput = await this.getAddToCartInputFromProps(props);
        const product = addToCartInput[0].product;
        const quantity = addToCartInput[0].count;

        const cartState = await getCartState(props.context.actionContext);
        const addToCartResult = await cartState.addProductToCart({
            product,
            count: quantity,
            availableQuantity: props.productAvailability?.AvailableQuantity,
            additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
            enableStockCheck: props.context.app.config.enableStockCheck,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.isPriceKeyedIn ? props.customPriceAmount : undefined,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
            shouldSkipSiteSettings: props.shouldSkipSiteSettings,
            catalogId: addToCartInput[0].catalogId
        });

        if (addToCartResult.status === 'SUCCESS') {
            console.log('its success');
        }
    };
    public _getCartStatusUpdate = (props: IAddToCartComponentProps, recordId: number): number => {
        const cartLinesData = props.cartData;
        if (cartLinesData.length !== 0) {
            const findData = cartLinesData.find((cartLinesData: { ItemId: any }) => cartLinesData.ItemId === recordId);
            if (findData) {
                return findData.Quantity;
            }
        }

        return 0;
        //return findData?.Quantity || 0;
    };
    public _SingleAddToCart = () => {
        void this.addOneItemToCart(this.props);
        this.setState({ currentProductqQty: 1 });
        return true;
    };

    public render(): JSX.Element | null {
        var getQty = this._getCartStatusUpdate(this.props, this.props.product.ItemId);
        //this.setState({ currentProductqQty: getQty });
        var button = null;
        var buttonAddtoCart = null;
        if (this.state.currentProductqQty === -1) {
            getQty = 0;
        }
        //this.setState({ currentProductqQty: getQty });
        if (
            getQty === 0 &&
            (this.state.currentProductqQty === 0 || this.state.currentProductqQty === '' || this.state.currentProductqQty === -1)
        ) {
            getQty = 0;
            // this.setState({ currentProductqQty: 0 });
            buttonAddtoCart = (
                <button className='local-plus-icon' onClick={this._SingleAddToCart}>
                    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <g clipPath='url(#clip0_840_2233)'>
                            <path
                                d='M10 0.771423V19.3429'
                                stroke='#610FD1'
                                strokeWidth='1.41697'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                            <path
                                d='M0.714355 10H19.2858'
                                stroke='#610FD1'
                                strokeWidth='1.41697'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </g>
                        <defs>
                            <clipPath id='clip0_840_2233'>
                                <rect width='20' height='20' fill='white' />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
            );
            return <Node className='nope'>{buttonAddtoCart}</Node>;
        }
        if (getQty === 1 && this.state.currentProductqQty === '') {
            button = (
                <button className='decrement quantity__controls' onClick={this.removeITem}>
                    <svg width='21' height='20' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <g clipPath='url(#clip0_840_2209)'>
                            <path d='M1 3.5H13' stroke='white' strokeLinecap='round' strokeLinejoin='round' />
                            <path
                                d='M2.5 3.5H11.5V12.5C11.5 12.7652 11.3946 13.0196 11.2071 13.2071C11.0196 13.3946 10.7652 13.5 10.5 13.5H3.5C3.23478 13.5 2.98043 13.3946 2.79289 13.2071C2.60536 13.0196 2.5 12.7652 2.5 12.5V3.5Z'
                                stroke='white'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                            <path
                                d='M4.5 3.5V3C4.5 2.33696 4.76339 1.70107 5.23223 1.23223C5.70107 0.763392 6.33696 0.5 7 0.5C7.66304 0.5 8.29893 0.763392 8.76777 1.23223C9.23661 1.70107 9.5 2.33696 9.5 3V3.5'
                                stroke='white'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </g>
                        <defs>
                            <clipPath id='clip0_840_2209'>
                                <rect width='21' height='20' fill='white' />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
            );
        }
        if (this.state.currentProductqQty === 1) {
            button = (
                <button className='decrement quantity__controls' onClick={this.removeITem}>
                    <svg width='21' height='20' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <g clipPath='url(#clip0_840_2209)'>
                            <path d='M1 3.5H13' stroke='white' strokeLinecap='round' strokeLinejoin='round' />
                            <path
                                d='M2.5 3.5H11.5V12.5C11.5 12.7652 11.3946 13.0196 11.2071 13.2071C11.0196 13.3946 10.7652 13.5 10.5 13.5H3.5C3.23478 13.5 2.98043 13.3946 2.79289 13.2071C2.60536 13.0196 2.5 12.7652 2.5 12.5V3.5Z'
                                stroke='white'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                            <path
                                d='M4.5 3.5V3C4.5 2.33696 4.76339 1.70107 5.23223 1.23223C5.70107 0.763392 6.33696 0.5 7 0.5C7.66304 0.5 8.29893 0.763392 8.76777 1.23223C9.23661 1.70107 9.5 2.33696 9.5 3V3.5'
                                stroke='white'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </g>
                        <defs>
                            <clipPath id='clip0_840_2209'>
                                <rect width='21' height='20' fill='white' />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
            );
        }
        return (
            <Node className='nope localy-quantity'>
                <div
                    className='quantity'
                    id={`msc-cart-line__quantity_${this.props.product.RecordId}-${this.props.product.RecordId}-${this.props.product.ItemId}`}
                >
                    {button}
                    <IncrementalQuantity
                        id={`msc-cart-line__quantity_${this.props.product.RecordId}-${this.props.product.RecordId}-${this.props.product.ItemId}`}
                        //id={`msc-cart-line__quantity_`}
                        max={20}
                        currentCount={this.state.currentProductqQty || getQty}
                        onChange={this.onChangeHandler}
                        inputQuantityAriaLabel={'resources.inputQuantityAriaLabel'}
                        decrementButtonAriaLabel={'extentionResources.decrementButtonAriaLabel'}
                        incrementButtonAriaLabel={'extentionResources.incrementButtonAriaLabel'}
                        minQuantityText={'extentionResources.minQuantityText'}
                        maxQuantityText={'extentionResources.maxQuantityText'}
                        itemId={this.props.product.ItemId}
                        //   telemetryContent={telemetryContent}
                    />
                </div>
            </Node>
        );
    }
}
export default ProductCollectionQty;
